import { computed, Injectable, signal } from "@angular/core";
import { DrawerItem } from "@progress/kendo-angular-layout";
import { MenuNode } from "../archive-view/reordering";
import { sample } from "../archive-view/sample/sample";

@Injectable({
  providedIn: "root",
})
export class MenuService {
  //--------------------------------------------------------------------------------------------------------------------
  // Variables privées
  //--------------------------------------------------------------------------------------------------------------------
  readonly #menuNode = signal<MenuNode[]>(sample);
  readonly #selectedDrawerItemId = signal<number | string | undefined>(
    undefined,
  );

  //--------------------------------------------------------------------------------------------------------------------
  // Variables public
  //--------------------------------------------------------------------------------------------------------------------
  node = this.#menuNode.asReadonly();
  selectedDrawerItemId = this.#selectedDrawerItemId.asReadonly();
  readonly expandable = signal<boolean>(true);
  readonly expandedDrawerIds = signal<number[]>([]);

  //--------------------------------------------------------------------------------------------------------------------
  // setter
  // this.#menuNode
  //--------------------------------------------------------------------------------------------------------------------
  setMenuNode(node: MenuNode[]): void {
    this.#menuNode.set([...node]);
    this.ensureSelectedMenuItem();
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Ajoute ou retire l’identifiant fourni de la liste des éléments du drawer actuellement dépliés
  //--------------------------------------------------------------------------------------------------------------------
  toggleExpandedDrawerId(id: number): void {
    this.expandedDrawerIds.update((ids) =>
      ids.includes(id)
        ? ids.filter((currentId) => currentId !== id)
        : [...ids, id],
    );
  }

  //--------------------------------------------------------------------------------------------------------------------
  //  Rest du paramètre Expanded
  //--------------------------------------------------------------------------------------------------------------------
  resetExpandedDrawerIds(): void {
    this.expandedDrawerIds.set(this.parentIds());
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Sélectionne automatiquement le premier élément du menu si l’élément actuellement sélectionné n’existe plus
  //--------------------------------------------------------------------------------------------------------------------
  ensureSelectedMenuItem(): void {
    const nodes = this.#menuNode();
    const selectedId = this.#selectedDrawerItemId();

    const selectedNodeExists = nodes.some(
      (node) => node.archiveId === selectedId,
    );

    if (selectedNodeExists) {
      return;
    }

    const firstNode = nodes[0];
    this.#selectedDrawerItemId.set(firstNode?.archiveId);
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Sélectionne un élément à une position id
  //--------------------------------------------------------------------------------------------------------------------
  public selectMenuItem(id: number | string | undefined): void {
    this.#selectedDrawerItemId.set(id);
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Retourne le texte de l’élément du menu actuellement sélectionné
  //--------------------------------------------------------------------------------------------------------------------
  selectedMenuText = computed<string>(() => {
    const selectedId = this.#selectedDrawerItemId();

    const selectedNode = this.#menuNode().find(
      (node) => node.archiveId === selectedId,
    );

    return selectedNode?.name || selectedNode?.label || "";
  });

  //--------------------------------------------------------------------------------------------------------------------
  // computed
  // Retourne les sous-éléments de l’élément sélectionné qui possèdent un type (pages de contenu)
  //--------------------------------------------------------------------------------------------------------------------
  selectedSubMenu = computed(() => {
    const selectedId = this.#selectedDrawerItemId();

    return this.#menuNode().filter(
      (node): node is MenuNode & { type: "Texte" | "Tableau" | "Figure" } =>
        node.parentId === selectedId && node.type !== "",
    );
  });

  //--------------------------------------------------------------------------------------------------------------------
  // Modifie le boolean MenuNode isEdit
  //--------------------------------------------------------------------------------------------------------------------
  switchEdit(archiveId: number): boolean {
    const node = this.#menuNode().find((n) => n.archiveId === archiveId);

    if (!node) {
      return false;
    }

    const isEdit = !("isEdit" in node ? node.isEdit : false);

    this.#menuNode.update((nodes) =>
      nodes.map((n) => (n.archiveId === archiveId ? { ...n, isEdit } : n)),
    );

    return isEdit;
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Ajoute un nouveau bloc Texte avant (swAjouterEnHaut) ou après l'élément fourni, dans le même parent
  // Valable pour la partie lecture
  //--------------------------------------------------------------------------------------------------------------------
  ajouter(archiveId: number, swAjouterEnHaut: boolean): number | undefined {
    const nodes = this.#menuNode();

    const index = nodes.findIndex((n) => n.archiveId === archiveId);
    if (index === -1) {
      return undefined;
    }

    const reference = nodes[index];
    const newArchiveId = Math.max(0, ...nodes.map((n) => n.archiveId)) + 1;

    const newNode: MenuNode = {
      archiveId: newArchiveId,
      parentId: reference.parentId,
      name: "",
      nodeType: reference.nodeType,
      staffId: "",
      label: "",
      phone: "",
      email: "",
      yearsExperience: null,
      isPage: false,
      patientCapacity: 0,
      certifications: [],
      hireDate: new Date(),
      isSelected: false,
      icon: reference.icon,
      type: "Texte",
      bloc: "",
      isEdit: false,
      lastEdit: new Date(),
    };

    this.#menuNode.update((current) => {
      const next = [...current];
      next.splice(swAjouterEnHaut ? index : index + 1, 0, newNode);
      return next;
    });

    return newArchiveId;
  }

  sauvagerderTexte(archiveId: number, value: string): void {
    console.log("SAVE", archiveId, value);

    this.#menuNode.update((nodes) =>
      nodes.map((n) =>
        n.archiveId === archiveId && "bloc" in n
          ? {
              ...n,
              bloc: value,
              lastEdit: new Date(),
            }
          : n,
      ),
    );
  }

  //--------------------------------------------------------------------------------------------------------------------
  // computed
  // Retourne la liste des identifiants des éléments du menu qui possèdent au moins un enfant
  //--------------------------------------------------------------------------------------------------------------------
  parentIds = computed<number[]>(() => {
    const nodes = this.#menuNode().filter((node) => node.type === "");

    return nodes
      .filter((node) =>
        nodes.some((child) => child.parentId === node.archiveId),
      )
      .map((node) => node.archiveId);
  });

  //--------------------------------------------------------------------------------------------------------------------
  // computed
  // Menu pour Drawer
  //--------------------------------------------------------------------------------------------------------------------
  menu = computed<DrawerItem[]>(() => {
    const selectedId = this.#selectedDrawerItemId();

    return this.#menuNode()
      .filter((node) => node.type === "")
      .map((node) => {
        const item: DrawerItem = {
          id: node.archiveId,
          text: node.name || node.label || `Item ${node.archiveId}`,
          icon: node.icon || "k-i-files-error",
          selected: node.archiveId === selectedId,
        };

        if (node.parentId !== null) {
          item.parentId = node.parentId;
        }

        return item;
      });
  });

  //--------------------------------------------------------------------------------------------------------------------
  // Toggle Menu
  //--------------------------------------------------------------------------------------------------------------------
  readonly drawerToggleRequest = signal<number>(0);

  public requestDrawerToggle(): void {
    this.drawerToggleRequest.update((value) => value + 1);
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Suppression d'un élément du menu
  //--------------------------------------------------------------------------------------------------------------------
  removeMenuNode(id: number | string): void {
    this.#menuNode.update((nodes) => {
      const idsToRemove = new Set<number | string>();

      const collectChildren = (parentId: number | string) => {
        nodes.forEach((node) => {
          if (node.parentId === parentId) {
            idsToRemove.add(node.archiveId);
            collectChildren(node.archiveId);
          }
        });
      };

      idsToRemove.add(id);
      collectChildren(id);

      return nodes.filter((node) => !idsToRemove.has(node.archiveId));
    });
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Compte le nombre d'enfants d'un élément sélectionné
  //--------------------------------------------------------------------------------------------------------------------
  countChildren(id: number | string): number {
    const nodes = this.#menuNode();

    let count = 0;

    const walk = (parentId: number | string) => {
      for (const node of nodes) {
        if (node.parentId === parentId) {
          count++;
          walk(node.archiveId);
        }
      }
    };

    walk(id);

    return count;
  }

  //--------------------------------------------------------------------------------------------------------------------
  // setter
  // à une position id, pour dire est sélectionné
  //--------------------------------------------------------------------------------------------------------------------
  setIsSelected(id: number): void {
    const node = this.#menuNode().map((item) => ({
      ...item,
      isSelected: item.archiveId === id,
    }));

    this.#menuNode.set([...node]);
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Affiche dans la console le JSON complet, puis chaque partie (enfant direct de la racine) avec toute sa descendance
  //--------------------------------------------------------------------------------------------------------------------
  logMenuNode(): void {
    const nodes = this.#menuNode();

    // 1) JSON complet
    console.log(JSON.stringify(nodes, null, 2));

    // 2) Séparateur vide
    console.log("");

    // 3) Une partie par enfant de la racine (ex: 1A, 1B), avec tous ses descendants
    const collectSubtree = (parent: MenuNode): MenuNode[] => [
      parent,
      ...nodes
        .filter((node) => node.parentId === parent.archiveId)
        .flatMap((child) => collectSubtree(child)),
    ];

    const roots = nodes.filter((node) => node.parentId === null);

    for (const root of roots) {
      for (const part of nodes.filter((n) => n.parentId === root.archiveId)) {
        console.log(JSON.stringify(collectSubtree(part), null, 2));
      }
    }
  }

  //--------------------------------------------------------------------------------------------------------------------
  // setter
  // à une position id, pour dire n'est pas sélectionné
  //--------------------------------------------------------------------------------------------------------------------
  setIsSelectedFalse(): void {
    const node = this.#menuNode().map((item) => ({
      ...item,
      isSelected: false,
    }));

    this.#menuNode.set([...node]);
  }
}
